import { Archive, PackagePlus, Pencil, Plus, Tag } from "lucide-react";
import { useState, type FormEvent } from "react";

import { EmptyState } from "../components/EmptyState";
import { CollectionSearch } from "../components/CollectionSearch";
import { CollectionView } from "../components/CollectionView";
import { ViewModeToggle } from "../components/ViewModeToggle";
import { matchesCollectionSearch } from "../lib/collection-search";
import { useSectionSearch } from "../lib/section-search";
import {
  collectionRecordSearchValues,
  supplierColumns,
  toSupplierRecord,
} from "../lib/purchasing-collection-records";
import { trpc } from "../lib/trpc";
import { useViewMode } from "../lib/view-mode";
import { MATERIAL_UNIT_SINGULAR_LABELS } from "../../shared/material-units";

const unitLabels = MATERIAL_UNIT_SINGULAR_LABELS;

type EditorMode = "supplier" | "material" | "offer" | null;

function nullable(value: FormDataEntryValue | null) {
  const text = String(value ?? "").trim();
  return text || null;
}

function centsFromEuros(value: FormDataEntryValue | null) {
  return Math.round(Number(String(value ?? "0").replace(",", ".")) * 100);
}

function formatEuros(cents: number | string) {
  return new Intl.NumberFormat("it-IT", { style: "currency", currency: "EUR" }).format(Number(cents) / 100);
}

export function SuppliersPage() {
  const utils = trpc.useUtils();
  const suppliers = trpc.suppliers.list.useQuery({ includeInactive: true });
  const materials = trpc.materials.list.useQuery();
  const offers = trpc.materials.listOffers.useQuery({ includeHistory: true });
  const createSupplier = trpc.suppliers.create.useMutation();
  const updateSupplier = trpc.suppliers.update.useMutation();
  const archiveSupplier = trpc.suppliers.archive.useMutation();
  const createMaterial = trpc.materials.create.useMutation();
  const archiveMaterial = trpc.materials.archive.useMutation();
  const addOffer = trpc.materials.addOffer.useMutation();
  const [mode, setMode] = useState<EditorMode>(null);
  const [editingSupplierId, setEditingSupplierId] = useState<string | null>(null);
  const [replacingOfferId, setReplacingOfferId] = useState<string | null>(null);
  const [offerMaterialId, setOfferMaterialId] = useState("");
  const [viewMode, setViewMode] = useViewMode("fornitori");
  const [notice, setNotice] = useState<string | null>(null);
  const [search, setSearch] = useSectionSearch();
  const [supplierStatus, setSupplierStatus] = useState<"all" | "active" | "inactive">("active");
  const editingSupplier = suppliers.data?.find((supplier) => supplier.id === editingSupplierId);
  const queryError = suppliers.error ?? materials.error ?? offers.error;
  const queryLoading = suppliers.isLoading || materials.isLoading || offers.isLoading;

  async function refreshSuppliers() {
    await utils.suppliers.list.invalidate();
  }

  async function refreshMaterials() {
    await Promise.all([
      utils.materials.list.invalidate(),
      utils.materials.listOffers.invalidate(),
    ]);
  }

  async function submitSupplier(event: FormEvent<HTMLFormElement>) {
    event.preventDefault();
    const form = event.currentTarget;
    const data = new FormData(form);
    const values = {
      name: String(data.get("name")),
      contactName: nullable(data.get("contactName")),
      email: nullable(data.get("email")),
      phone: nullable(data.get("phone")),
      address: nullable(data.get("address")),
      notes: nullable(data.get("notes")),
    };
    try {
      if (editingSupplierId) await updateSupplier.mutateAsync({ id: editingSupplierId, data: values });
      else await createSupplier.mutateAsync(values);
      await refreshSuppliers();
      setNotice(editingSupplierId ? "Fornitore aggiornato." : "Fornitore creato.");
      setEditingSupplierId(null);
      setMode(null);
      form.reset();
    } catch {
      // L'errore della mutation viene mostrato nel modulo.
    }
  }

  async function submitMaterial(event: FormEvent<HTMLFormElement>) {
    event.preventDefault();
    const form = event.currentTarget;
    const data = new FormData(form);
    try {
      await createMaterial.mutateAsync({
        name: String(data.get("name")),
        category: String(data.get("category")),
        description: nullable(data.get("description")),
        baseUnit: String(data.get("baseUnit")) as keyof typeof unitLabels,
      });
      await refreshMaterials();
      setNotice("Materiale aggiunto al catalogo.");
      setMode(null);
      form.reset();
    } catch {
      // L'errore della mutation viene mostrato nel modulo.
    }
  }

  async function submitOffer(event: FormEvent<HTMLFormElement>) {
    event.preventDefault();
    const form = event.currentTarget;
    const data = new FormData(form);
    const materialId = String(data.get("materialId"));
    const material = materials.data?.find((item) => item.id === materialId);
    if (!material) return;
    try {
      await addOffer.mutateAsync({
        supplierId: String(data.get("supplierId")),
        materialId,
        replacesOfferId: replacingOfferId,
        brandVariant: nullable(data.get("brandVariant")),
        packageDescription: String(data.get("packageDescription")),
        packageQuantity: Number(data.get("packageQuantity")),
        packageUnit: material.baseUnit,
        packagePriceCents: centsFromEuros(data.get("packagePrice")),
        notes: nullable(data.get("notes")),
      });
      await refreshMaterials();
      setNotice(replacingOfferId ? "Prezzo aggiornato e storico conservato." : "Prezzo aggiunto al listino.");
      setReplacingOfferId(null);
      setOfferMaterialId("");
      setMode(null);
      form.reset();
    } catch {
      // L'errore della mutation viene mostrato nel modulo.
    }
  }

  async function confirmArchiveSupplier(id: string, name: string) {
    if (!window.confirm(`Archiviare il fornitore ${name}?`)) return;
    await archiveSupplier.mutateAsync({ id });
    await refreshSuppliers();
  }

  async function confirmArchiveMaterial(id: string, name: string) {
    if (!window.confirm(`Archiviare il materiale ${name}?`)) return;
    await archiveMaterial.mutateAsync({ id });
    await refreshMaterials();
  }

  function editSupplier(id: string) {
    setEditingSupplierId(id);
    setMode("supplier");
  }

  function replaceOffer(offer: NonNullable<typeof offers.data>[number]) {
    setReplacingOfferId(offer.id);
    setOfferMaterialId(offer.materialId);
    setMode("offer");
  }

  const supplierRecords = (suppliers.data ?? [])
    .filter((supplier) => supplierStatus === "all" || (supplierStatus === "active" ? supplier.active : !supplier.active))
    .map((supplier, index) => toSupplierRecord(
      supplier,
      (offers.data ?? [])
        .filter((offer) => offer.supplierId === supplier.id && !offer.supersededAt)
        .map((offer) => ({
          offer,
          materialName: materials.data?.find((material) => material.id === offer.materialId)?.name ?? "Materiale",
        })),
      <div className="supplier-card__actions">
        <button className="icon-button" type="button" aria-label={`Modifica ${supplier.name}`} onClick={() => editSupplier(supplier.id)}><Pencil size={17} /></button>
        <button className="icon-button" type="button" aria-label={`Archivia ${supplier.name}`} onClick={() => void confirmArchiveSupplier(supplier.id, supplier.name)}><Archive size={17} /></button>
      </div>,
      index,
    ))
    .filter((record) => matchesCollectionSearch(search, collectionRecordSearchValues(record)));

  return (
    <section className="purchase-page">
      <header className="page-heading">
        <div><p className="eyebrow">Rubrica e listini</p><h1>Fornitori</h1><p>Contatti, materiali e prezzi delle confezioni in un unico posto.</p></div>
        <div className="page-heading__actions">
          <ViewModeToggle value={viewMode} onChange={setViewMode} />
          <div className="button-group">
            <button className="button button--outline" type="button" onClick={() => { setEditingSupplierId(null); setMode("supplier"); }}><Plus size={18} /> Nuovo fornitore</button>
            <button className="button button--outline" type="button" onClick={() => setMode("material")}><PackagePlus size={18} /> Materiale</button>
            <button className="button button--primary" type="button" onClick={() => { setReplacingOfferId(null); setOfferMaterialId(""); setMode("offer"); }}><Tag size={18} /> Nuovo prezzo</button>
          </div>
        </div>
      </header>

      {notice && <p className="notice" role="status">{notice}</p>}

      <div className="purchase-filters">
        <CollectionSearch label="Cerca fornitori" value={search} onChange={setSearch} placeholder="Nome, referente, contatto o offerta" />
        <label><span>Stato fornitori</span><select value={supplierStatus} onChange={(event) => setSupplierStatus(event.target.value as typeof supplierStatus)}><option value="active">Attivi</option><option value="inactive">Inattivi</option><option value="all">Tutti</option></select></label>
      </div>

      {queryError && <div className="query-error" role="alert"><strong>Dati fornitori non disponibili</strong><span>{queryError.message}</span><button className="button button--outline" type="button" onClick={() => void Promise.all([suppliers.refetch(), materials.refetch(), offers.refetch()])}>Riprova</button></div>}
      {!queryError && queryLoading && <p className="loading-copy">Caricamento fornitori e listini...</p>}

      {mode === "supplier" && (
        <form className="editor-card" key={editingSupplierId ?? "new-supplier"} onSubmit={submitSupplier}>
          <div className="editor-card__heading"><h2>{editingSupplier ? "Modifica fornitore" : "Nuovo fornitore"}</h2><p>I recapiti saranno disponibili anche durante la preparazione dell'ordine.</p></div>
          <div className="form-grid">
            <label><span>Nome fornitore</span><input name="name" defaultValue={editingSupplier?.name} required /></label>
            <label><span>Referente</span><input name="contactName" defaultValue={editingSupplier?.contactName ?? ""} /></label>
            <label><span>Email fornitore</span><input name="email" type="email" defaultValue={editingSupplier?.email ?? ""} /></label>
            <label><span>Telefono</span><input name="phone" type="tel" defaultValue={editingSupplier?.phone ?? ""} /></label>
            <label className="form-grid__wide"><span>Indirizzo fornitore</span><input name="address" defaultValue={editingSupplier?.address ?? ""} /></label>
            <label className="form-grid__wide"><span>Note</span><textarea name="notes" rows={3} defaultValue={editingSupplier?.notes ?? ""} /></label>
          </div>
          {(createSupplier.error || updateSupplier.error) && <p className="form-error" role="alert">{createSupplier.error?.message ?? updateSupplier.error?.message}</p>}
          <div className="form-actions"><button className="button button--ghost" type="button" onClick={() => setMode(null)}>Annulla</button><button className="button button--primary" disabled={createSupplier.isPending || updateSupplier.isPending}>Salva fornitore</button></div>
        </form>
      )}

      {mode === "material" && (
        <form className="editor-card" onSubmit={submitMaterial}>
          <div className="editor-card__heading"><h2>Nuovo materiale</h2><p>La stessa unità sarà usata per confrontare correttamente i prezzi.</p></div>
          <div className="form-grid">
            <label><span>Nome materiale</span><input name="name" required placeholder="Cloro granulare" /></label>
            <label><span>Categoria</span><input name="category" required placeholder="Piscina" /></label>
            <label><span>Unità base</span><select name="baseUnit" defaultValue="piece">{Object.entries(unitLabels).map(([value, label]) => <option key={value} value={value}>{label}</option>)}</select></label>
            <label className="form-grid__wide"><span>Descrizione</span><textarea name="description" rows={3} /></label>
          </div>
          {createMaterial.error && <p className="form-error" role="alert">{createMaterial.error.message}</p>}
          <div className="form-actions"><button className="button button--ghost" type="button" onClick={() => setMode(null)}>Annulla</button><button className="button button--primary" disabled={createMaterial.isPending}>Salva materiale</button></div>
        </form>
      )}

      {mode === "offer" && (
        <form className="editor-card" key={replacingOfferId ?? "new-offer"} onSubmit={submitOffer}>
          <div className="editor-card__heading"><h2>{replacingOfferId ? "Aggiorna prezzo" : "Nuovo prezzo fornitore"}</h2><p>Inserisci confezione e prezzo: MANUTIO calcola il costo per unità.</p></div>
          <div className="form-grid">
            <label><span>Fornitore</span><select name="supplierId" required defaultValue={offers.data?.find((offer) => offer.id === replacingOfferId)?.supplierId ?? ""}><option value="" disabled>Seleziona</option>{suppliers.data?.map((supplier) => <option key={supplier.id} value={supplier.id}>{supplier.name}</option>)}</select></label>
            <label><span>Materiale</span><select name="materialId" required value={offerMaterialId} onChange={(event) => setOfferMaterialId(event.target.value)}><option value="" disabled>Seleziona</option>{materials.data?.map((material) => <option key={material.id} value={material.id}>{material.name}</option>)}</select></label>
            <label><span>Marca o variante</span><input name="brandVariant" /></label>
            <label><span>Descrizione confezione</span><input name="packageDescription" required placeholder="Secchio da 10 kg" /></label>
            <label><span>Quantità nella confezione</span><input name="packageQuantity" type="number" min="0.0001" step="any" required /></label>
            <label><span>Prezzo confezione in euro</span><input name="packagePrice" inputMode="decimal" required placeholder="42,00" /></label>
            <label className="form-grid__wide"><span>Note listino</span><textarea name="notes" rows={3} /></label>
          </div>
          {addOffer.error && <p className="form-error" role="alert">{addOffer.error.message}</p>}
          <div className="form-actions"><button className="button button--ghost" type="button" onClick={() => setMode(null)}>Annulla</button><button className="button button--primary" disabled={addOffer.isPending}>Salva prezzo</button></div>
        </form>
      )}

      {!queryError && !queryLoading && <section className="supplier-section">
        <div className="section-title"><div><p className="eyebrow">Rubrica</p><h2>Elenco fornitori</h2></div><span>{supplierRecords.length}</span></div>
        {supplierRecords.length > 0 && <CollectionView
          mode={viewMode}
          ariaLabel="Fornitori"
          titleLabel="Fornitore"
          columns={supplierColumns}
          records={supplierRecords}
          print={{
            section: "Fornitori",
            details: [
              supplierStatus === "all" ? null : supplierStatus === "active" ? "Solo attivi" : "Solo non attivi",
              search ? `Ricerca: ${search}` : null,
            ],
          }}
        />}
        {supplierRecords.length === 0 && ((suppliers.data?.length ?? 0) === 0
          ? <EmptyState title="Nessun fornitore">Aggiungi il primo fornitore.</EmptyState>
          : <EmptyState title="Nessun risultato">Modifica la ricerca o il filtro per vedere altri fornitori.</EmptyState>)}
      </section>}

      {!queryError && !queryLoading && <section className="supplier-section">
        <div className="section-title"><div><p className="eyebrow">Catalogo acquisti</p><h2>Materiali e storico prezzi</h2></div><span>{materials.data?.length ?? 0}</span></div>
        <div className="material-list">
          {materials.data?.map((material) => {
            const materialOffers = offers.data?.filter((offer) => offer.materialId === material.id) ?? [];
            return (
              <article className="material-card" key={material.id}>
                <header><div><h3>{material.name}</h3><p>{material.category} · per {unitLabels[material.baseUnit]}</p></div><button className="icon-button" type="button" aria-label={`Archivia ${material.name}`} onClick={() => void confirmArchiveMaterial(material.id, material.name)}><Archive size={17} /></button></header>
                {materialOffers.length > 0 ? <ul>{materialOffers.map((offer) => { const supplier = suppliers.data?.find((item) => item.id === offer.supplierId); const isCurrent = !offer.supersededAt; return <li key={offer.id}><span><strong>{supplier?.name ?? "Fornitore"}</strong><small>{offer.packageDescription}{offer.brandVariant ? ` · ${offer.brandVariant}` : ""}</small><small>Aggiornato il {new Intl.DateTimeFormat("it-IT", { dateStyle: "long" }).format(new Date(offer.createdAt))}</small></span><span><strong>{formatEuros(offer.packagePriceCents)}</strong><small>{formatEuros(offer.unitPriceCents)} / {unitLabels[offer.packageUnit]}</small><small className={isCurrent ? "offer-state offer-state--current" : "offer-state offer-state--history"}>{isCurrent ? "Prezzo attuale" : "Prezzo superato"}</small></span>{isCurrent && <button className="text-button" type="button" onClick={() => replaceOffer(offer)}>Aggiorna</button>}</li>; })}</ul> : <p className="muted-copy">Nessun prezzo inserito.</p>}
              </article>
            );
          })}
        </div>
      </section>}
    </section>
  );
}
